import Stack from '@mui/material/Stack'
import { alpha, type SxProps, useTheme } from '@mui/material/styles'

import { H3, MerchantLogo, Text } from '@mxenabled/mxui'
import { CheckCircle } from '@mxenabled/mx-icons'

interface Merchant {
  categoryGuid: string
  merchantGuid: string
}

interface P2PMultipleCategorizationProps {
  content?: string
  merchants: Merchant[]
  sx?: SxProps
}

const P2PMultipleCategorization = ({ content, merchants, sx }: P2PMultipleCategorizationProps) => {
  const theme = useTheme()
  const shadow = `8px 6px 0 ${alpha(theme.palette.common.black, 0.75)}`

  const hasFourMerchants = merchants.length >= 4
  const gap = hasFourMerchants ? 8 : 24
  const justifyContent = hasFourMerchants ? 'inherit' : 'center'

  const mtOffsets = (() => {
    switch (merchants.length) {
      case 2:
        return [0, 36]
      case 3:
        return [0, 18, 36]
      case 4:
      default:
        return [0, 12, 24, 36]
    }
  })()

  return (
    <Stack
      sx={{
        bgcolor: 'neutral.lighter',
        gap: 12,
        height: content ? 174 : 138,
        p: 16,
        width: 320,
        ...sx,
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap,
          justifyContent,
          height: 106,
          width: 288,
        }}
      >
        {merchants.slice(0, 4).map((merchant, i) => {
          const isMoreIndicator = i === 3 && hasFourMerchants

          return (
            <Stack
              key={i}
              sx={{
                borderRadius: '8px',
                boxShadow: shadow,
                height: 'max-content',
                mt: mtOffsets[i],
                pl: i ? 1 : 0,
              }}
            >
              {isMoreIndicator ? (
                <Stack
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'grey.800',
                    borderRadius: '8px',
                    height: 63,
                    justifyContent: 'center',
                    outline: '1px solid',
                    outlineColor: 'background.paper',
                    width: 63,
                  }}
                >
                  <H3 sx={{ color: 'common.white' }}>+{merchants.length - 3}</H3>
                </Stack>
              ) : (
                <MerchantLogo
                  borderRadius={1}
                  size={63}
                  sx={{ outline: '1px solid', outlineColor: 'background.paper' }}
                  {...merchant}
                />
              )}
            </Stack>
          )
        })}
      </Stack>
      {content && (
        <Stack
          sx={{
            alignItems: 'center',
            bgcolor: 'success.lighter',
            border: '1px solid',
            borderColor: 'success.main',
            borderRadius: '999px',
            flexDirection: 'row',
            gap: 6,
            justifyContent: 'center',
          }}
        >
          <CheckCircle filled={true} size={16} sx={{ color: 'success.dark' }} />
          <Text color="success.dark" sx={{ fontWeight: 400 }} variant="subtitle2">
            {content}
          </Text>
        </Stack>
      )}
    </Stack>
  )
}

export default P2PMultipleCategorization
